'use client';

import { ReactNode, useState, useEffect } from 'react';
import { useLocationPathname } from '@/app/features/workspace/routing/useWorkspacePath';
import Header from '@/app/components/layout/Header';
import Container from '@/app/components/layout/Container';
import AppBreadcrumb from '@/app/components/layout/AppBreadcrumb';
import { useBranding } from '@/app/hooks/useBranding';

interface LayoutProps {
  children: ReactNode;
  showHeader?: boolean;
  showFooter?: boolean;
  headerAutoHide?: boolean;
  headerCompact?: boolean;
  /** Render the in-app breadcrumb. Off for marketing/landing pages. */
  showBreadcrumb?: boolean;
  containerSize?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  containerPadding?: 'none' | 'sm' | 'md' | 'lg';
  density?: 'compact' | 'default' | 'comfortable';
  /**
   * Lock the layout to exactly the viewport height on desktop — outer div
   * becomes `h-screen overflow-hidden` so no window scrollbar appears.
   * Use for pages whose children manage their own internal scrolling (e.g.
   * the workspace AppShell with sticky sidebar + scrollable main). Leave
   * off for long-form pages (marketing landing, settings forms outside
   * the shell) that genuinely want document-flow scroll.
   */
  lockViewportHeight?: boolean;
  className?: string;
}

const Layout: React.FC<LayoutProps> = ({
  children,
  showHeader = true,
  showFooter = false,
  headerAutoHide = false,
  headerCompact = false,
  showBreadcrumb = true,
  containerSize = 'lg',
  containerPadding = 'md',
  density = 'default',
  lockViewportHeight = false,
  className = ''
}) => {
  useLocationPathname(); // re-render on client navigation (static-export safe)
  const { copyrightHolder } = useBranding();
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  // Toggle the html-level scrollbar-gutter override for lock-viewport pages.
  // The global rule in globals.css reserves a permanent scrollbar gutter on
  // <html> to prevent navigation layout-shifts; for pages that manage their
  // own internal scroll (workspace AppShell, /main) that gutter shows as a
  // dead track on the right edge. Adding `lock-viewport-height` swaps it to
  // `overflow-y: hidden; scrollbar-gutter: auto;` for the duration of the
  // page, then cleans up on unmount so other pages get their gutter back.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    if (lockViewportHeight) {
      root.classList.add('lock-viewport-height');
      return () => {
        root.classList.remove('lock-viewport-height');
      };
    }
  }, [lockViewportHeight]);

  // Density-based spacing
  const densityClasses = {
    compact: {
      spacing: 'space-y-3',
      padding: 'py-3',
      gap: 'gap-3'
    },
    default: {
      spacing: 'space-y-4',
      padding: 'py-4',
      gap: 'gap-4'
    },
    comfortable: {
      spacing: 'space-y-6',
      padding: 'py-6',
      gap: 'gap-6'
    }
  };

  const densityStyle = densityClasses[density];

  // Determine if footer should show based on showFooter prop
  const shouldShowFooter = showFooter;

  if (!isClient) {
    return null;
  }

  return (
    <div className={`min-h-screen flex flex-col bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-slate-900 dark:to-indigo-950 transition-colors duration-300 ${lockViewportHeight ? 'md:h-screen md:overflow-hidden' : ''} ${className}`}>
      {/* Header */}
      {showHeader && (
        <Header 
          autoHide={headerAutoHide} 
          compact={headerCompact} 
        />
      )}

      {/* Main Content Area with header offset.
          `bg-background` ensures the body's gradient never peeks through
          between main content and the footer on authenticated shell pages.
          Marketing pages (/main, /login) override with their own hero bg,
          so the gradient stays visible where it's intentional. */}
      <main className={`flex-1 bg-background ${densityStyle.padding} ${showHeader ? (headerCompact ? 'pt-10' : 'pt-12') : ''}`}>
        <Container
          size={containerSize}
          padding={containerPadding}
          className={densityStyle.spacing}
        >
          {showBreadcrumb && <AppBreadcrumb />}
          {children}
        </Container>
      </main>

      {/* Footer — desktop-only; mobile apps conventionally omit legal footers (Instagram/Gmail/Notion pattern) */}
      {shouldShowFooter && (
        <footer
          className="hidden md:block flex-shrink-0 border-t border-border/20 shadow-lg backdrop-blur-sm"
          style={{
            background: 'linear-gradient(to right, hsl(var(--primary)), hsl(var(--accent)))',
            minHeight: '48px'
          }}
        >
          <Container size={containerSize} padding={containerPadding}>
            <div className={`flex items-center justify-center ${headerCompact ? 'h-10' : 'h-12'}`}>
              <p className="text-white/90 text-sm text-center font-medium">
                © {new Date().getFullYear()} {copyrightHolder}. All rights reserved.
              </p>
            </div>
          </Container>
        </footer>
      )}
    </div>
  );
};

export default Layout;